import cloneDeep from 'lodash/cloneDeep';
import mapValues from 'lodash/mapValues';
import { useCallback, useEffect, useRef } from 'react'; // RAGBOT: useRef added
import { useForm } from 'react-hook-form';
import { useRecoilState, useSetRecoilState } from 'recoil';

import {
  chatSettingsInputsState,
  chatSettingsValueState,
  useChatData,
  useChatInteract
} from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Translator } from 'components/i18n';

import { chatSettingsOpenState } from 'state/project';

import { FormInput, TFormInputValue } from './FormInput';
import { useChatSettingsSnapshotAtOpen } from './useChatSettingsSnapshotAtOpen';

/* RAGBOT: upstream declares no constants or helpers in this file; the whole
   block below is ragbot-only. */

// RAGBOT: mirrors SettingsService.INPUT_MESSAGE_MAX_LENGTH_ID. DO NOT CHANGE.
export const INPUT_MESSAGE_MAX_LENGTH_ID = 'max-input-length';
const LLM_NAME_ID = 'llm-name'; // RAGBOT: mirrors SettingsService.LLM_NAME_KEY
const isHiddenInput = (input: any) => input?.id === INPUT_MESSAGE_MAX_LENGTH_ID; // RAGBOT

// RAGBOT: upstream inlines this predicate; extracted so the same rule classifies
// tabs before and after filtering.
const isTabInput = (input: any) =>
  Array.isArray(input?.inputs) && input.inputs.length > 0;

// RAGBOT: extracted from upstream's inline mapValues in handleConfirm, reused by
// the live model apply.
const toPayload = (values: Record<string, TFormInputValue>) =>
  mapValues(values, (x: TFormInputValue) => (x !== '' ? x : null));

// RAGBOT: the max-input-length widget carries the composer limit and is never
// rendered, at the top level or inside a tab group.
const withoutHiddenWidgets = (inputs: any[]) =>
  inputs
    .filter((input: any) => !isHiddenInput(input))
    .map((input: any) =>
      isTabInput(input)
        ? {
            ...input,
            inputs: input.inputs.filter((child: any) => !isHiddenInput(child))
          }
        : input
    );

export default function ChatSettingsModal() {
  // RAGBOT: chatSettingsDefaultValue is unused upstream; it feeds reset and the
  // live model apply below.
  const { chatSettingsValue, chatSettingsInputs, chatSettingsDefaultValue } =
    useChatData();

  const { updateChatSettings, editChatSettings } = useChatInteract();
  const [chatSettingsOpen, setChatSettingsOpen] = useRecoilState(
    chatSettingsOpenState
  );

  const { valuesAtOpen, inputsAtOpen } = useChatSettingsSnapshotAtOpen(
    chatSettingsOpen,
    chatSettingsValue,
    chatSettingsInputs
  );

  const { handleSubmit, setValue, reset, watch, getValues } = useForm({
    defaultValues: chatSettingsValue
  });
  const setChatSettingsValue = useSetRecoilState(chatSettingsValueState);
  const setChatSettingsInputs = useSetRecoilState(chatSettingsInputsState);

  /* RAGBOT: ragbot-only. Upstream's snapshot is taken when the dialog opens, so
     it describes the previous model's widgets and goes stale the moment the
     model select is applied live. These refs hold a replacement snapshot. */
  const liveSnapshotRef = useRef<{
    values: Record<string, any>;
    inputs: unknown[];
  } | null>(null);
  const awaitingLiveWidgetsRef = useRef(false);

  useEffect(() => {
    if (!chatSettingsOpen) {
      liveSnapshotRef.current = null; // RAGBOT: each dialog session starts clean
      awaitingLiveWidgetsRef.current = false;
    }
  }, [chatSettingsOpen]);

  // RAGBOT: the backend answers a live model change by re-sending the widget
  // list; snapshot that instead of the state the dialog opened with.
  useEffect(() => {
    if (!awaitingLiveWidgetsRef.current) return;
    awaitingLiveWidgetsRef.current = false;
    liveSnapshotRef.current = {
      values: cloneDeep(chatSettingsValue),
      inputs: cloneDeep(chatSettingsInputs)
    };
  }, [chatSettingsInputs, chatSettingsValue]);

  const restoreSnapshot = useCallback(() => {
    // RAGBOT: upstream always restores valuesAtOpen / inputsAtOpen; prefer the
    // post-live-apply snapshot when the model was changed.
    const { values, inputs } = liveSnapshotRef.current ?? {
      values: valuesAtOpen,
      inputs: inputsAtOpen
    };
    setChatSettingsInputs(cloneDeep(inputs));
    setChatSettingsValue(cloneDeep(values));
  }, [inputsAtOpen, setChatSettingsInputs, setChatSettingsValue, valuesAtOpen]);

  // Reset form when default values change
  useEffect(() => {
    reset(chatSettingsValue);
  }, [chatSettingsValue, reset]);

  const handleClose = (open: boolean) => {
    if (!open) {
      restoreSnapshot();
      setChatSettingsOpen(false);
    }
  };

  const handleConfirm = handleSubmit((data) => {
    const processedValues = toPayload(data); // RAGBOT: was an inline mapValues
    updateChatSettings(processedValues);
    setChatSettingsValue(processedValues);
    setChatSettingsOpen(false);
  });

  /* RAGBOT: upstream reset calls restoreSnapshot, which duplicates cancel. Here
     reset restores the initial values the backend declared for the current
     model. */
  const handleReset = () => {
    reset(chatSettingsDefaultValue);
  };

  /* RAGBOT: upstream leaves handleChange empty because only SelectInput still
     fires onChange. Ragbot reuses that free slot for the model select: it is
     applied immediately so on_settings_update can re-send the matching
     parameter widgets. Every other field keeps the upstream confirm/cancel
     flow through setFieldValue below. */
  const handleChange = (field: string, value: any) => {
    if (field !== LLM_NAME_ID) return;

    const newValues = { ...chatSettingsDefaultValue, [field]: value };
    reset(newValues);
    awaitingLiveWidgetsRef.current = true; // RAGBOT: re-snapshot once widgets arrive
    updateChatSettings(toPayload(newValues)); // RAGBOT: emits chat_settings_change
  };

  const setFieldValue = (field: string, value: any) => {
    setValue(field, value);
    editChatSettings(getValues());
  };

  const values = watch();

  // RAGBOT: upstream splits chatSettingsInputs directly; hidden widgets are
  // dropped here so no render site has to filter again.
  const visibleInputs = withoutHiddenWidgets(chatSettingsInputs);
  const tabInputs = visibleInputs.filter(isTabInput);
  const regularInputs = visibleInputs.filter(
    (input: any) => !isTabInput(input)
  );
  const hasTabs = tabInputs.length > 0;
  const defaultTab = tabInputs[0]?.id;

  return (
    <Dialog open={chatSettingsOpen} onOpenChange={handleClose}>
      <DialogContent
        id="chat-settings"
        className={`flex flex-col gap-6 p-6 ${
          hasTabs ? 'min-w-[25vw] h-[85vh]' : 'min-w-[20vw] max-h-[85vh]'
        }`}
      >
        <DialogHeader>
          <DialogTitle>
            <Translator path="chat.settings.title" />
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Translator path="chat.settings.customize" />
          </DialogDescription>
        </DialogHeader>
        {hasTabs ? (
          <Tabs
            defaultValue={defaultTab}
            className="flex flex-col flex-grow min-h-0"
          >
            <TabsList className="w-full flex justify-start">
              {tabInputs.map((tab: any) => (
                <TabsTrigger key={tab.id} value={tab.id}>
                  {tab.label ?? tab.id}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabInputs.map((tab: any) => (
              <TabsContent
                key={tab.id}
                value={tab.id}
                className="data-[state=active]:flex flex-col flex-grow overflow-y-auto gap-6 p-1 mt-4"
              >
                {tab.inputs?.map((input: any) => (
                  <FormInput
                    key={input.id}
                    element={{
                      ...input,
                      value: values[input.id],
                      // RAGBOT: upstream passes handleChange bare; the id is
                      // needed to recognise the model select.
                      onChange: (value: any) => handleChange(input.id, value),
                      setField: setFieldValue
                    }}
                  />
                ))}
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <div className="flex flex-col flex-grow overflow-y-auto gap-6 p-1">
            {regularInputs.map((input: any) => (
              <FormInput
                key={input.id}
                element={{
                  ...input,
                  value: values[input.id],
                  // RAGBOT: same id-carrying wrapper as in the tabs branch.
                  onChange: (value: any) => handleChange(input.id, value),
                  setField: setFieldValue
                }}
              />
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleReset}>
            <Translator path="common.actions.reset" />
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => handleClose(false)}>
            <Translator path="common.actions.cancel" />
          </Button>
          <Button onClick={handleConfirm} id="confirm" autoFocus>
            <Translator path="common.actions.confirm" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
